import { AlertTriangle, CheckCircle, Info, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

const FallbackLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 239 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M196.498 10.4573H205.321L213.533 24.6222L221.772 10.4573H230.595V29.0604H226.449V10.6433L215.739 29.0604H211.354L200.617 10.6433V29.0604H196.498V10.4573Z" fill="#2E0368"></path>
    <path d="M169.921 21.8317C166.785 21.8317 164.261 19.307 164.261 16.1445C164.261 13.0086 166.785 10.4573 169.921 10.4573H193.972V14.6031H169.921C169.071 14.6031 168.38 15.2941 168.38 16.1445C168.38 17.0215 169.071 17.7125 169.921 17.7125H188.285C191.421 17.7125 193.972 20.2372 193.972 23.3997C193.972 26.5357 191.421 29.0604 188.285 29.0604H164.261V24.9411H188.285C189.136 24.9411 189.827 24.2501 189.827 23.3997C189.827 22.5227 189.136 21.8317 188.285 21.8317H169.921Z" fill="#2E0368"></path>
    <path d="M154.857 10.4573C158.843 10.4573 162.086 13.6995 162.086 17.7125C162.086 21.6989 158.843 24.9411 154.857 24.9411H136.254V29.0604H132.135V10.4573H154.857ZM154.83 20.7953C156.558 20.7953 157.94 19.4133 157.94 17.7125C157.94 15.9851 156.558 14.6031 154.83 14.6031H136.254V20.7953H154.83Z" fill="#2E0368"></path>
    <path d="M109.975 14.6031C107.105 14.6031 104.793 16.9152 104.793 19.7588C104.793 22.629 107.105 24.9411 109.975 24.9411H125.336V21.6989H109.576V17.5796H129.482V29.0604H109.975C104.819 29.0604 100.673 24.9145 100.673 19.7588C100.673 14.6297 104.819 10.4573 109.975 10.4573H129.482V14.6031H109.975Z" fill="#2E0368"></path>
    <path d="M74 29.0604V10.4573H78.1193V24.9411H98.9281V29.0604H74Z" fill="#2E0368"></path>
    <path d="M238.311 9.41768C238.311 10.7529 237.228 11.8354 235.893 11.8354C234.558 11.8354 233.475 10.7529 233.475 9.41768C233.475 8.08243 234.558 7 235.893 7C237.228 7 238.311 8.08243 238.311 9.41768Z" fill="#2E0368"></path>
    <path d="M32 10H42C47.5228 10 52 14.4772 52 20C52 25.5228 47.5228 30 42 30H36V28H42C46.4183 28 50 24.4183 50 20C50 15.5817 46.4183 12 42 12H32V22H42C43.1046 22 44 21.1046 44 20C44 18.8954 43.1046 18 42 18H36V16H42C44.2091 16 46 17.7909 46 20C46 22.2091 44.2091 24 42 24H32V34H42C49.732 34 56 27.732 56 20C56 12.268 49.732 6 42 6H36V4H42C50.8366 4 58 11.1634 58 20C58 28.8366 50.8366 36 42 36H32V40H42C53.0457 40 62 31.0457 62 20C62 8.95431 53.0457 0 42 0H32V10Z" fill="#4D0DA2"></path>
    <path d="M20 0C8.95431 0 0 8.95431 0 20C0 31.0457 8.95431 40 20 40H30V30H20C14.4772 30 10 25.5228 10 20C10 14.4772 14.4772 10 20 10H26V12H20C15.5817 12 12 15.5817 12 20C12 24.4183 15.5817 28 20 28H30V18H20C18.8954 18 18 18.8954 18 20C18 21.1046 18.8954 22 20 22H26V24H20C17.7909 24 16 22.2091 16 20C16 17.7909 17.7909 16 20 16H30V6H20C12.268 6 6 12.268 6 20C6 27.732 12.268 34 20 34H26V36H20C11.1634 36 4 28.8366 4 20C4 11.1634 11.1634 4 20 4H30V0H20Z" fill="#4D0DA2"></path>
  </svg>
);

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const buildLogoUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE}/${path.replace(/^\/+/, "")}`;
};

const VARIANT_FIELD = {
  header: "header_logo",
  footer: "footer_logo",
  "dark-header": "dark_header_logo",
  "dark-footer": "dark_footer_logo",
};

export const CompanyLogo = ({ className, variant = "header", dark = false }) => {
  const siteSettings = useSiteSettings();
  const website = siteSettings?.website || {};
  const key = dark ? `dark-${variant}` : variant;
  const field = VARIANT_FIELD[key] || VARIANT_FIELD[variant];
  const fallbackField = dark ? VARIANT_FIELD[variant] : null;

  const primary = website[field] || null;
  const lightFallback = fallbackField ? (website[fallbackField] || null) : null;

  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (primary) { setSrc(primary); setFailed(false); }
  }, [primary]);

  const handleError = () => {
    if (!failed && lightFallback && src !== lightFallback) {
      setSrc(lightFallback);
    } else {
      setFailed(true);
    }
  };

  if (failed || !src) return <FallbackLogo className={className} />;

  return (
    <img
      src={src}
      alt="Logo"
      className={`object-contain ${className || ""}`}
      onError={handleError}
      style={{ minWidth: 1, minHeight: 1 }}
    />
  );
};


const iconMap = {
  success: <CheckCircle className="text-green-500" size={20} />,
  error: <XCircle className="text-red-500" size={20} />,
  warning: <AlertTriangle className="text-yellow-500" size={20} />,
  info: <Info className="text-blue-500" size={20} />,
};

export const ToastMessage = ({
  type = "info",
  title = "Notification",
  message = "",
  onYes,
  onNo,
  toastType = "default",
}) => {
  const isConfirm = toastType === "confirm";

  const ConfirmContent = ({ closeToast }) => {
    const cardRef = useRef(null);

    useEffect(() => {
      if (!isConfirm) return;

      const handleClickOutside = (event) => {
        if (cardRef.current && !cardRef.current.contains(event.target)) {
          closeToast();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [closeToast]);

    return (
      <div
        ref={cardRef}
        className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:bg-slate-800"
      >
        <div className="flex items-center gap-3">
          <div className="mt-1">{iconMap[type]}</div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800 text-sm dark:text-gray-100">
              {title}
            </h3>
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-300">
              {message}
            </p>
          </div>
        </div>
        {isConfirm && (
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={closeToast}
              className="px-3 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200 transition dark:bg-slate-700 dark:text-gray-100 dark:hover:bg-slate-600"
            >
              No
            </button>
            {onYes && (
              <button
                onClick={() => {
                  onYes();
                  closeToast();
                }}
                className="px-3 py-1 text-xs rounded-md bg-orange-500 text-white hover:bg-orange-600 transition"
              >
                Yes
              </button>
            )}
          </div>
        )}
        {!isConfirm && (
          <div className="h-1 w-full bg-gray-200 rounded-full mt-4 overflow-hidden">
            <div className="h-full bg-orange-500 animate-progress group-hover:[animation-play-state:paused]" />
          </div>
        )}
      </div>
    );
  };

  toast(
    ({ closeToast }) => <ConfirmContent closeToast={closeToast} />,
    {
      toastId: isConfirm ? "confirm-toast" : undefined,
      position: "top-center",
      autoClose: isConfirm ? false : 2000,
      closeOnClick: !isConfirm,
      draggable: !isConfirm,
      hideProgressBar: true,
      toastClassName: "bg-transparent shadow-none p-0",
      bodyClassName: "p-0",
      style: {
        background: "transparent",
        padding: 0,
        boxShadow: "none",
      },
    }
  );
};
export const formatTime = (time) => {
  const [hour, minute] = time.split(":").map(Number);

  const period = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;

  return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
};

export const getSelectedIds = (selectedRows) => {
  return (selectedRows ?? []).filter((id) => id != null);
};
export const getUserFromToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    return {
      id: decoded.id || decoded.user_id || null,
      name: decoded.name || decoded.username || 'User',
      email: decoded.email || 'user@example.com',
      role: decoded.role || 0,
      role_id: decoded.role_id || 0,
      user_image: decoded.user_image || null,
    };
  } catch (error) {
    return null;
  }
};
export const getImg = (name) => {
  if (!name) return name;
  if (/^https?:\/\//i.test(name)) return name;
  return `${process.env.PUBLIC_URL}/images/${name}`;
};


export const handleExportExcel = async ({response, file_name}) => {
  const url = window.URL.createObjectURL(response);

  const link = document.createElement("a");

  link.href = url;

  link.setAttribute(
    "download",
    file_name
  );

  document.body.appendChild(link);

  link.click();

  link.remove();

  window.URL.revokeObjectURL(url);
}
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "923343446406";

export const buildWhatsappUrl = (product) => {
  const baseUrl = process.env.REACT_APP_WEBSITE_BASE_URL || "https://ukashaenterprises.com";
  const productUrl = `${baseUrl}/products/${product.slug}`;


  const msg = encodeURIComponent(
    `Hi! I'm interested in ordering:\n\n` +
    `*${product.title ?? product.name ?? ""}*\n` +
    `Product Link: ${productUrl}\n\n` +
    `Please let me know the availability.`
  );

  return `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
};
